import { motion } from "framer-motion";
import { CheckCircle2, ChevronRight, Clock, Flag, Plus, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtDate, slugify } from "@/lib/aba";
import { ACTIONS } from "@/constants/testIds";

export const StatusBadge = ({ stato, className = "" }) =>
  stato === "acquisito" ? (
    <Badge className={`bg-emerald-50 text-emerald-900 border border-emerald-200 hover:bg-emerald-50 gap-1 ${className}`}><CheckCircle2 className="h-3 w-3" /> Acquisito</Badge>
  ) : (
    <Badge className={`bg-accent text-foreground border border-border hover:bg-accent gap-1 ${className}`}><span className="h-1.5 w-1.5 rounded-full bg-blue-600" /> Introdotto</Badge>
  );

export const ActionsSidebar = ({ actions = [], isLoading, selectedId, onSelect, onNew, domainLabel }) => {
  return (
    <aside className="bg-card border border-border rounded-xl flex flex-col min-h-[420px] lg:h-[calc(100vh-260px)] lg:sticky lg:top-24" data-testid={ACTIONS.sidebar}>
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{domainLabel}</p>
            <h3 className="font-heading font-semibold text-base">Azioni <span className="text-muted-foreground font-normal">({actions.length})</span></h3>
          </div>
          <Button size="sm" className="gap-1.5" onClick={onNew} data-testid={ACTIONS.newButton}>
            <Plus className="h-4 w-4" /> Aggiungi
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Ogni azione ha il suo grafico indipendente.</p>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {isLoading && [0, 1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
          {!isLoading && actions.length === 0 && (
            <div className="px-3 py-10 text-center" data-testid={ACTIONS.emptyState}>
              <p className="text-sm text-muted-foreground">Nessuna azione in questo dominio.</p>
              <Button variant="link" className="text-primary" onClick={onNew}>Crea la prima azione</Button>
            </div>
          )}
          {actions.map((a) => {
            const selected = a.action_id === selectedId;
            return (
              <button
                key={a.action_id}
                type="button"
                onClick={() => onSelect(a.action_id)}
                data-testid={`action-row-${slugify(a.nome)}`}
                data-action-id={a.action_id}
                aria-current={selected ? "true" : undefined}
                className={`relative w-full text-left rounded-lg px-3 py-2.5 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${selected ? "bg-accent" : "hover:bg-muted"}`}
              >
                {selected && <motion.span layoutId="action-indicator" className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full bg-primary" />}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{a.nome}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <StatusBadge stato={a.stato} className="text-[10px] px-1.5 py-0" />
                      {a.suggerisci_acquisito && <span className="inline-flex items-center gap-1 text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5"><Sparkles className="h-3 w-3" /> criterio</span>}
                      {a.in_ritardo && <span className="inline-flex items-center gap-1 text-[10px] text-rose-800 bg-rose-50 border border-rose-200 rounded px-1.5" data-testid={`action-late-${slugify(a.nome)}`}><Flag className="h-3 w-3" /> in ritardo</span>}
                      {!a.in_ritardo && a.in_scadenza && <span className="inline-flex items-center gap-1 text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5" data-testid={`action-due-${slugify(a.nome)}`}><Flag className="h-3 w-3" /> scade {a.giorni_all_obiettivo === 0 ? "oggi" : `tra ${a.giorni_all_obiettivo} gg`}</span>}
                      {a.stale && <span className="inline-flex items-center gap-1 text-[10px] text-rose-800 bg-rose-50 border border-rose-200 rounded px-1.5" data-testid={`action-stale-${slugify(a.nome)}`}><Clock className="h-3 w-3" /> {a.giorni_inattivita} gg</span>}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {a.stato === "acquisito" ? `Acquisito il ${fmtDate(a.data_acquisizione)}` : `Introdotto il ${fmtDate(a.data_introduzione)}`}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-heading font-semibold text-lg leading-none tabular" data-testid={`action-sessions-count-${slugify(a.nome)}`}>{a.n_sessioni}</p>
                    <p className="text-[10px] text-muted-foreground">sessioni</p>
                  </div>
                </div>
                {!selected && <ChevronRight className="h-4 w-4 text-muted-foreground absolute right-2 bottom-2.5 opacity-0 group-hover:opacity-100" />}
              </button>
            );
          })}
        </div>
      </ScrollArea>
    </aside>
  );
};
